/**
 * Formatação WhatsApp, upload de mídia e reescrita com IA do editor.
 */
import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import { notify } from '../../../../utils/toast';
import { rewriteFollowUpTemplateMessage } from '../../../../services/geminiService';
import { useMediaUpload } from '../../../chat/hooks/useMediaUpload';
import { parseMediaUrls, serializeMediaUrls } from '../followUpMedia';
import {
    WHATSAPP_WRAP,
    clearWhatsAppMarkers,
    prefixSelectedLines,
    wrapSelection,
    type WhatsAppFormat,
} from '../followUpWhatsAppFormat';

export type MediaPickKind = 'document' | 'media' | 'audio';

export function useFollowUpMessageEditor(options: {
    value: string;
    onChange: (text: string) => void;
    mediaUrl?: string | null;
    onMediaChange: (url: string | null) => void;
    fieldId: string;
}) {
    const { value, onChange, mediaUrl, onMediaChange, fieldId } = options;
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [rewriting, setRewriting] = useState(false);
    const { uploadToR2 } = useMediaUpload(undefined);
    const mediaUrls = parseMediaUrls(mediaUrl);

    const setMediaUrls = useCallback((urls: string[]) => {
        onMediaChange(serializeMediaUrls(urls));
    }, [onMediaChange]);

    const applyEdit = useCallback((
        compute: (text: string, start: number, end: number) => {
            next: string;
            selectionStart: number;
            selectionEnd: number;
        },
    ) => {
        const el = textareaRef.current;
        const text = value ?? '';
        const start = el?.selectionStart ?? text.length;
        const end = el?.selectionEnd ?? text.length;
        const { next, selectionStart, selectionEnd } = compute(text, start, end);
        onChange(next);
        requestAnimationFrame(() => {
            const node = textareaRef.current;
            if (!node) return;
            node.focus();
            node.setSelectionRange(selectionStart, selectionEnd);
        });
    }, [onChange, value]);

    const runFormat = (command: WhatsAppFormat) => {
        if (command === 'clear') {
            applyEdit(clearWhatsAppMarkers);
            return;
        }
        if (command === 'bulletList') {
            applyEdit((text, start, end) => prefixSelectedLines(text, start, end, 'bullet'));
            return;
        }
        if (command === 'orderedList') {
            applyEdit((text, start, end) => prefixSelectedLines(text, start, end, 'ordered'));
            return;
        }
        const { open, close } = WHATSAPP_WRAP[command];
        applyEdit((text, start, end) => wrapSelection(text, start, end, open, close));
    };

    const openFilePicker = (accept: string, multiple: boolean) => {
        const input = fileInputRef.current;
        if (!input) return;
        input.accept = accept;
        input.multiple = multiple;
        input.click();
    };

    const handleMediaPick = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        event.target.value = '';
        if (files.length === 0) return;

        setUploading(true);
        try {
            const uploaded: string[] = [];
            for (const file of files) uploaded.push(await uploadToR2(file));
            setMediaUrls([...mediaUrls, ...uploaded]);
            notify.success(uploaded.length > 1 ? `${uploaded.length} arquivos adicionados.` : 'Mídia adicionada.');
        } catch (error) {
            console.error('[FollowUpMessageEditor] upload error:', error);
            notify.error(error instanceof Error ? error.message : 'Erro ao enviar mídia.');
        } finally {
            setUploading(false);
        }
    };

    const handleRewriteWithAi = async () => {
        if (rewriting) return;
        if (!(value ?? '').trim()) {
            notify.warning(
                mediaUrls.length > 0
                    ? 'Escreva uma legenda/mensagem no texto antes de reescrever com IA. A mídia anexada não é reescrita.'
                    : 'Escreva uma mensagem antes de reescrever com IA.',
            );
            return;
        }
        setRewriting(true);
        try {
            const next = await rewriteFollowUpTemplateMessage(value, { hasMedia: mediaUrls.length > 0 });
            onChange(next);
            notify.success('Mensagem reescrita com IA.');
            requestAnimationFrame(() => {
                const node = textareaRef.current;
                if (!node) return;
                node.focus();
                node.setSelectionRange(next.length, next.length);
            });
        } catch (error) {
            console.error('[FollowUpMessageEditor] rewrite error:', error);
            notify.error(error instanceof Error ? error.message : 'Erro ao reescrever com IA.');
        } finally {
            setRewriting(false);
        }
    };

    return {
        textareaRef,
        fileInputRef,
        uploading,
        rewriting,
        mediaUrls,
        applyEdit,
        runFormat,
        openFilePicker,
        handleMediaPick,
        removeMediaAt: (index: number) => setMediaUrls(mediaUrls.filter((_, i) => i !== index)),
        handleRewriteWithAi,
        fileInputId: `${fieldId}-file-attachment-input`,
        messageInputId: `${fieldId}-message-input`,
        messageInputName: `${fieldId}-message`,
    };
}
